import type { AuthenticatedUserContract, Locale } from "@chams-pay/contracts";
import { operationsMessages, translate } from "@chams-pay/contracts";
import { Button } from "@chams-pay/design-system/components/button";
import { CalendarRange, ChevronDown, Plus, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  casablancaDate,
  dashboardPeriodPresetBounds,
  type DashboardPeriodPreset,
} from "../core/format.js";
import { screenTitle, workspaceNavigation, type WorkspaceScreen } from "../core/navigation.js";
import type { ServerSessionState } from "../core/use-session.js";
import {
  useDashboard,
  useInstruments,
  useReports,
  type InstrumentStatusFilter,
} from "../core/use-operations.js";
import {
  DEFAULT_STUDENT_FILTERS,
  type StudentFilters,
  type StudentSearchController,
} from "../core/use-students.js";
import type { WorkspaceController } from "../core/use-workspace.js";
import {
  relativeAgeLabel,
  updateBannerSupersedesServerSessionNotice,
  workspaceBanner,
} from "../core/use-workspace.js";
import { DatePicker } from "../ui/date-picker.js";
import { DateValue } from "../ui/figures.js";
import { DesktopDashboardOffline, DesktopDashboardScreen } from "./screens/dashboard.js";
import { DesktopInstrumentsScreen } from "./screens/instruments.js";
import { DesktopReportsScreen } from "./screens/reports.js";
import { DesktopSettingsScreen } from "./screens/settings.js";
import { DesktopStudentsScreen } from "./screens/students.js";
import { AppSidebar, LanguageSwitch, SiteHeader, WorkspaceBanner } from "./shell.js";

type MessageKey = Parameters<typeof translate>[1];

function ServerSessionNotice({
  locale,
  state,
  onRetry,
  onSignIn,
}: {
  readonly locale: Locale;
  readonly state: Exclude<ServerSessionState, "ready">;
  readonly onRetry: () => void;
  readonly onSignIn: () => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  const unreachable = state === "unreachable";
  const restoring = state === "restoring";
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-3 rounded-lg border border-warning-border bg-warning-soft px-4 py-3 text-warning"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <strong className="font-heading text-sm">
          {t(
            restoring
              ? "entry.sessionRestoringTitle"
              : unreachable
                ? "entry.serverUnreachableTitle"
                : "entry.onlineSessionRequiredTitle",
          )}
        </strong>
        <span className="text-xs leading-relaxed">
          {t(
            restoring
              ? "entry.sessionRestoringBody"
              : unreachable
                ? "entry.serverUnreachableBody"
                : "entry.onlineSessionRequiredBody",
          )}
        </span>
      </span>
      {restoring ? (
        <RefreshCw aria-hidden="true" className="size-4 animate-spin" />
      ) : (
        <span className="flex flex-wrap gap-2">
          {unreachable && (
            <Button type="button" variant="outline" size="sm" onClick={onRetry}>
              <RefreshCw aria-hidden="true" />
              {t("ops.retry")}
            </Button>
          )}
          <Button type="button" size="sm" onClick={onSignIn}>
            {t("entry.signInOnline")}
          </Button>
        </span>
      )}
    </div>
  );
}

/** Feeds the sidebar's cheque-and-transfer count without owning the screen. */
function InstrumentCountProbe({
  csrf,
  onCount,
}: {
  readonly csrf: string;
  readonly onCount: (count: number) => void;
}) {
  const controller = useInstruments(csrf);
  const count = controller.items.filter((item) => item.status === "pending").length;
  useEffect(() => {
    onCount(count);
  }, [count, onCount]);
  return null;
}

function PeriodChip({
  locale,
  academicYear,
  from,
  to,
  onFrom,
  onTo,
}: {
  readonly locale: Locale;
  readonly academicYear: string | undefined;
  readonly from: string;
  readonly to: string;
  readonly onFrom: (value: string) => void;
  readonly onTo: (value: string) => void;
}) {
  const text = operationsMessages(locale);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const today = casablancaDate();
  const choosePreset = (preset: DashboardPeriodPreset) => {
    const bounds = dashboardPeriodPresetBounds(preset, academicYear, today);
    onFrom(bounds.from);
    onTo(bounds.to);
  };
  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (rootRef.current?.contains(target) || target.closest("[data-calendar-popover]")) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open]);
  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="chams-focusable flex h-8 items-center gap-2 rounded-full border border-input bg-card px-2.5 shadow-e1 transition-colors duration-fast hover:bg-surface-2"
      >
        <CalendarRange aria-hidden="true" className="size-3.5 text-muted-2" />
        <span className="chams-figure text-[11px] font-bold">
          <DateValue value={from} /> – <DateValue value={to} />
        </span>
        <ChevronDown aria-hidden="true" className="size-3.5 text-muted-2" />
      </button>
      {open && (
        <div className="absolute end-0 top-full z-40 mt-2 flex w-72 flex-col gap-3 rounded-lg border border-border bg-popover p-4 shadow-e3 animate-fade-up">
          <strong className="font-heading text-sm">
            {translate(locale, "dash.activityPeriod")}
          </strong>
          <div className="grid grid-cols-3 gap-1.5">
            {(["week", "month", "academic-year"] as const).map((preset) => (
              <Button
                key={preset}
                type="button"
                variant="outline"
                size="sm"
                className="h-8 px-2 text-[10px]"
                onClick={() => choosePreset(preset)}
              >
                {translate(
                  locale,
                  preset === "week"
                    ? "dash.periodWeek"
                    : preset === "month"
                      ? "dash.periodMonth"
                      : "dash.periodAcademicYear",
                )}
              </Button>
            ))}
          </div>
          <span className="chams-eyebrow">{translate(locale, "dash.periodCustom")}</span>
          <label className="flex flex-col gap-1.5 text-xs text-muted-foreground">
            {text.periodFrom}
            <DatePicker
              locale={locale}
              label={text.periodFrom}
              value={from}
              max={to}
              onChange={onFrom}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-xs text-muted-foreground">
            {text.periodTo}
            <DatePicker
              locale={locale}
              label={text.periodTo}
              value={to}
              min={from}
              max={today}
              onChange={onTo}
            />
          </label>
        </div>
      )}
    </div>
  );
}

/**
 * Online-only screens mount their controllers lazily so an offline session
 * never opens a socket it cannot use. The dashboard owns its sticky header
 * because its refresh action uses the dashboard controller.
 */
function DashboardHost({
  locale,
  academicYear,
  blocked,
  banner,
  languageSwitch,
  sidebarCollapsed,
  sidebarToggleLabel,
  onSidebarToggle,
  onOpenStudents,
  onOpenInstruments,
}: {
  readonly locale: Locale;
  readonly academicYear: string | undefined;
  readonly blocked: boolean;
  readonly banner: React.ReactNode;
  readonly languageSwitch: React.ReactNode;
  readonly sidebarCollapsed: boolean;
  readonly sidebarToggleLabel: string;
  readonly onSidebarToggle: () => void;
  readonly onOpenStudents: (filters?: StudentFilters) => void;
  readonly onOpenInstruments: () => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  const controller = useDashboard();

  return (
    <>
      <SiteHeader
        title={t("dash.dashTitle")}
        sidebarCollapsed={sidebarCollapsed}
        sidebarToggleLabel={sidebarToggleLabel}
        onSidebarToggle={onSidebarToggle}
        actions={
          <>
            {languageSwitch}
            <Button
              variant="outline"
              size="icon"
              aria-label={t("ops.retry")}
              className="size-[38px]"
              disabled={controller.busy}
              onClick={() => {
                void controller.reload();
              }}
            >
              <RefreshCw className={controller.busy ? "animate-spin" : undefined} />
            </Button>
            <span aria-hidden="true" className="mx-1 h-6 w-px bg-border" />
            <Button
              className="h-[38px] min-h-[38px] px-3.5 text-xs"
              onClick={() => onOpenStudents()}
            >
              <Plus />
              {t("dash.takePayment")}
            </Button>
          </>
        }
      />
      <div className="shell-content flex flex-1 flex-col gap-3.5 overflow-auto bg-background p-5">
        {banner}
        <DesktopDashboardScreen
          locale={locale}
          controller={controller}
          blocked={blocked}
          activityPeriodSelector={
            <PeriodChip
              locale={locale}
              academicYear={controller.dashboard?.currentAcademicYear ?? academicYear}
              from={controller.from}
              to={controller.to}
              onFrom={controller.setFrom}
              onTo={controller.setTo}
            />
          }
          onOpenStudents={onOpenStudents}
          onOpenInstruments={onOpenInstruments}
        />
      </div>
    </>
  );
}

function InstrumentsHost({
  locale,
  csrf,
  onOpenStudent,
  initialFilter,
  onFilterChange,
}: {
  readonly locale: Locale;
  readonly csrf: string;
  readonly onOpenStudent: (studentId: string) => void;
  readonly initialFilter: InstrumentStatusFilter;
  readonly onFilterChange: (filter: InstrumentStatusFilter) => void;
}) {
  return (
    <DesktopInstrumentsScreen
      locale={locale}
      csrf={csrf}
      controller={useInstruments(csrf, { initialFilter, onFilterChange })}
      onOpenStudent={onOpenStudent}
    />
  );
}

function ReportsHost({ locale }: { readonly locale: Locale }) {
  return <DesktopReportsScreen locale={locale} controller={useReports()} />;
}

export function DesktopWorkspace({
  locale,
  user,
  csrf,
  online,
  networkOnline,
  serverSession,
  screen,
  onScreenChange,
  workspace,
  students,
  updateReady,
  updateBlocked,
  updateActivating,
  onActivateUpdate,
  onLocaleChange,
  onLock,
  onLogout,
  onSignInOnline,
  onRetryServerSession,
  onOpenInstrumentStudent,
  onStudentBack,
  onStudentListOpen,
}: {
  readonly locale: Locale;
  readonly user: AuthenticatedUserContract | null;
  readonly csrf: string | null;
  readonly online: boolean;
  readonly networkOnline: boolean;
  readonly serverSession: ServerSessionState;
  readonly screen: WorkspaceScreen;
  readonly onScreenChange: (screen: WorkspaceScreen) => void;
  readonly workspace: WorkspaceController;
  readonly students: StudentSearchController;
  readonly updateReady: boolean;
  readonly updateBlocked: boolean;
  readonly updateActivating: boolean;
  readonly onActivateUpdate: () => void;
  readonly onLocaleChange: () => void;
  readonly onLock: () => void;
  readonly onLogout: () => void;
  readonly onSignInOnline: () => void;
  readonly onRetryServerSession: () => void;
  readonly onOpenInstrumentStudent: (studentId: string) => void;
  readonly onStudentBack: () => void;
  readonly onStudentListOpen: () => void;
}) {
  const operationsText = operationsMessages(locale);
  const navigation = workspaceNavigation(locale, user);
  const [instrumentCount, setInstrumentCount] = useState(0);
  const [instrumentFilter, setInstrumentFilter] = useState<InstrumentStatusFilter>("pending");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const bannerKind = workspaceBanner({
    captureBlocked: workspace.captureBlocked,
    online: networkOnline,
    syncFailed: workspace.syncFailed,
    updateReady,
    updateBlocked,
  });
  const banner = (
    <WorkspaceBanner
      locale={locale}
      kind={bannerKind}
      workspace={workspace}
      updateActivating={updateActivating}
      onActivateUpdate={onActivateUpdate}
    />
  );
  const sessionNotice =
    serverSession !== "ready" && !updateBannerSupersedesServerSessionNotice(bannerKind) ? (
      <ServerSessionNotice
        locale={locale}
        state={serverSession}
        onRetry={onRetryServerSession}
        onSignIn={onSignInOnline}
      />
    ) : null;
  const languageSwitch = <LanguageSwitch locale={locale} onChange={onLocaleChange} />;
  const openStudents = (filters?: StudentFilters) => {
    onStudentListOpen();
    students.clearSelection();
    if (filters) students.setQuery("");
    students.setFilters(filters ?? DEFAULT_STUDENT_FILTERS);
    onScreenChange("students");
  };

  return (
    <div className="app-shell flex h-dvh overflow-hidden bg-canvas" data-testid="app-shell">
      {online && csrf && <InstrumentCountProbe csrf={csrf} onCount={setInstrumentCount} />}
      <AppSidebar
        locale={locale}
        user={user}
        items={navigation}
        activeScreen={screen}
        online={online}
        workspace={workspace}
        instrumentCount={instrumentCount}
        collapsed={sidebarCollapsed}
        onCollapsedChange={setSidebarCollapsed}
        onSelect={onScreenChange}
        onLock={onLock}
        onLogout={onLogout}
      />
      <main className="shell-main flex min-w-0 flex-1 flex-col py-2.5 pe-3">
        <div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card shadow-e1">
          {screen === "dashboard" && online ? (
            <DashboardHost
              locale={locale}
              academicYear={workspace.replica.students[0]?.enrollment.academicYear}
              blocked={workspace.captureBlocked}
              banner={banner}
              languageSwitch={languageSwitch}
              sidebarCollapsed={sidebarCollapsed}
              sidebarToggleLabel={translate(locale, "dash.workspace")}
              onSidebarToggle={() => setSidebarCollapsed((collapsed) => !collapsed)}
              onOpenStudents={openStudents}
              onOpenInstruments={() => {
                setInstrumentFilter("pending");
                onScreenChange("instruments");
              }}
            />
          ) : (
            <>
              <SiteHeader
                title={screenTitle(screen, locale, user)}
                actions={languageSwitch}
                sidebarCollapsed={sidebarCollapsed}
                sidebarToggleLabel={translate(locale, "dash.workspace")}
                onSidebarToggle={() => setSidebarCollapsed((collapsed) => !collapsed)}
              />
              <div className="shell-content flex flex-1 flex-col gap-3.5 overflow-auto bg-background p-5">
                {banner}
                {sessionNotice}
                {screen === "students" && (
                  <DesktopStudentsScreen
                    locale={locale}
                    online={online}
                    csrf={csrf}
                    user={user}
                    students={students}
                    workspace={workspace}
                    onBack={onStudentBack}
                  />
                )}
                {screen === "dashboard" && !online && (
                  <DesktopDashboardOffline
                    locale={locale}
                    lastSyncLabel={relativeAgeLabel(workspace.syncAgeMilliseconds, locale)}
                    onTakePayment={() => openStudents()}
                    onRetry={() => void workspace.refreshLocalStatus()}
                  />
                )}
                {screen === "instruments" && online && csrf && (
                  <InstrumentsHost
                    locale={locale}
                    csrf={csrf}
                    onOpenStudent={onOpenInstrumentStudent}
                    initialFilter={instrumentFilter}
                    onFilterChange={setInstrumentFilter}
                  />
                )}
                {screen === "instruments" && online && !csrf && (
                  <div className="empty-state flex flex-col items-center gap-3 rounded-lg border border-dashed border-border bg-card/50 p-10 text-center">
                    <strong className="font-heading text-base">
                      {translate(locale, "entry.onlineSessionRequiredTitle")}
                    </strong>
                    <p className="max-w-[46ch] text-sm text-muted-foreground">
                      {translate(locale, "entry.onlineSessionRequiredBody")}
                    </p>
                    <Button type="button" onClick={onSignInOnline}>
                      {translate(locale, "entry.signInOnline")}
                    </Button>
                  </div>
                )}
                {screen === "reports" && online && <ReportsHost locale={locale} />}
                {screen !== "students" &&
                  screen !== "settings" &&
                  screen !== "dashboard" &&
                  !online && (
                    <div className="empty-state rounded-lg border border-dashed border-border bg-card/50 p-10 text-center text-sm text-muted-foreground">
                      {operationsText.onlineOnly}
                    </div>
                  )}
                {screen === "settings" && (
                  <DesktopSettingsScreen
                    locale={locale}
                    user={user}
                    csrf={csrf}
                    onAdvanceYearActivated={() => void workspace.sync()}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
